'use client';

import { gql, useMutation } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import clsx from 'clsx';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { isRequired } from '../../util/formValidators';
import FormError from '../FormError';

interface FormValues {
  name: string;
  email: string;
}

const HeroForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      email: '',
    },
  });
  const [reqAddCustomerLeads, { loading, error, data, reset: resetMutation }] = useMutation(gql`
    mutation AddCustomerLeads($input: AddCustomerLeadsInput) {
      addCustomerLeads(input: $input) {
        code
        message
        content
      }
    }
  `);
  const isRequestOk = data ? String(data.addCustomerLeads?.code) === '200' : false;
  const responseError = data && !isRequestOk ? new Error(data.addCustomerLeads?.content || '') : null;

  const onSubmit = values => {
    resetMutation();
    reqAddCustomerLeads({
      variables: {
        input: { name: values.name, email: values.email },
      },
    }).then(({ data }) => {
      if (String(data.addCustomerLeads?.code) === '200') {
        setSubmitted(true);
      }
    });
  };

  if (submitted) {
    return (
      <div className='py-5 text-center'>
        <div className='mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#F0F5F0] text-[26px]'>
          ✅
        </div>
        <h3 className='font-playfair mb-1.5 text-[20px] font-bold text-[#1a2e1b]'>Check your inbox!</h3>
        <p className='text-base text-[#777]'>
          Your Backyard Bird ID Guide, 10% discount code, and Birding Journal are on their way. Welcome to Kingsyard!
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='mb-1.5 font-poppins text-[11px] font-bold uppercase tracking-[0.18em] text-primary'>
        Free · Instant
      </div>
      <div className='font-playfair mb-4 text-[22px] font-bold leading-tight text-[#1a2e1b]'>
        Get Your Free Welcome Bundle
      </div>
      <ul className='mb-5 list-none'>
        <li className='mb-2 flex items-start gap-2.5 text-base leading-relaxed text-[#444]'>
          <span className='mt-0.5 inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[#F0F5F0] text-[11px] font-bold text-primary'>
            ✓
          </span>
          <span>
            <strong>Backyard Bird ID Guide</strong> — your complete setup &amp; species reference (PDF)
          </span>
        </li>
        <li className='mb-2 flex items-start gap-2.5 text-base leading-relaxed text-[#444]'>
          <span className='mt-0.5 inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[#F0F5F0] text-[11px] font-bold text-primary'>
            ✓
          </span>
          <span>
            <strong>10% discount code</strong> — off your first Kingsyard order
          </span>
        </li>
        <li className='mb-2 flex items-start gap-2.5 text-base leading-relaxed text-[#444]'>
          <span className='mt-0.5 inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[#F0F5F0] text-[11px] font-bold text-primary'>
            ✓
          </span>
          <span>Seasonal birding tips &amp; expert advice by email</span>
        </li>
      </ul>
      <div className='mb-3'>
        <input
          type='text'
          placeholder='Your first name'
          autoComplete='given-name'
          {...register('name', { validate: isRequired })}
          className={`h-14 w-full rounded-[10px] border-[1.5px] bg-[#fafaf8] px-4 font-sans text-base text-[#1a2e1b] outline-none transition-colors placeholder_text-[#aaa] focus_border-primary focus_shadow-[0_0_0_3px_rgba(47,97,50,0.1)] ${errors.name ? 'border-red' : 'border-[#EDE9E2]'}`}
        />
        <ErrorMessage
          errors={errors}
          name='name'
          render={({ message }) => <p className='mt-1 text-left text-sm text-red'>{message}</p>}
        />
      </div>
      <div className='mb-3'>
        <input
          type='email'
          placeholder='Your email address'
          autoComplete='email'
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Za-zd0-9]+([-_.][A-Za-zd0-9]+)*@([A-Za-zd]+[-.])+[A-Za-zd]{2,5}$/,
              message: 'Invalid email address',
            },
          })}
          className={`h-14 w-full rounded-[10px] border-[1.5px] bg-[#fafaf8] px-4 font-sans text-base text-[#1a2e1b] outline-none transition-colors placeholder_text-[#aaa] focus_border-primary focus_shadow-[0_0_0_3px_rgba(47,97,50,0.1)] ${errors.email ? 'border-red' : 'border-[#EDE9E2]'}`}
        />
        <ErrorMessage
          errors={errors}
          name='email'
          render={({ message }) => <p className='mt-1 text-left text-sm text-red'>{message}</p>}
        />
      </div>
      <button
        type='submit'
        disabled={loading}
        className='group relative flex h-14 w-full cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-[10px] border-none bg-primary px-10 font-poppins text-[18px] font-semibold text-white transition-all duration-[250ms] hover_-translate-y-0.5 hover_bg-[#245027] hover_shadow-[0_8px_24px_rgba(0,0,0,0.2)]'
      >
        <span className='duration-[400ms] pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.12)] to-transparent transition-transform group-hover_translate-x-full' />
        <span className={clsx(loading ? 'invisible' : '')} />&nbsp;
        <span className={clsx(loading ? 'invisible' : '')}>Send Me the Free Guide</span>
        <div
          className={clsx(
            'absolute left-1/2 top-1/2 z-[2] h-auto w-5 -translate-x-1/2 -translate-y-1/2 ',
            loading ? 'block' : 'hidden',
          )}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            className='h-auto w-5 animate-spin text-white [animation-duration:1s]'
            viewBox='0 0 24 24'
          >
            <circle
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
              className='opacity-25'
            ></circle>
            <path
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              className='opacity-75'
            ></path>
          </svg>
        </div>
      </button>
      {error || responseError ? (
        <div className='text-red'>
          <FormError
            errors={[error, responseError].filter(Boolean)}
            scrollOnError={false}
            allowErrorMessages
          />
        </div>
      ) : null}
      <div className='mt-2.5 text-center text-[11.5px] leading-normal text-[#aaa]'>
        No spam, ever. Unsubscribe anytime. We respect your privacy.
      </div>
    </form>
  );
};

export default HeroForm;
